import { useFormContext } from '@/lib/form-contexts'
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  DEFAULT_DELIVERY_AREA_FILL_OPACITY,
  DELIVERY_AREA_COLORS,
  getSemanticPolygonColor,
  getSemanticPolygonFillOpacity,
  getZIndexForPolygonType,
} from '../constants'
import {
  encodedPolylineToGeoJson,
  encodePolygonFromGeoJson,
  selectTerraDrawFeature,
} from '../utils'

const FIELD_NAME = 'delivery_area_fees'

/**
 * Utility functions for polygon matching and comparison
 */
const matchPolygonById = (feature, polygonData) => {
  if (feature.id === polygonData.reference_id) return true
  if (polygonData.id && feature.properties?.databaseId === polygonData.id) return true
  if (polygonData.reference_id && feature.properties?.referenceId === polygonData.reference_id)
    return true
  return false
}

const matchPolygonByGeometry = (feature, formPolygon, geometry) => {
  if (!formPolygon.encoded_polygon) return false

  try {
    const formGeoJson = encodedPolylineToGeoJson(formPolygon.encoded_polygon, geometry)
    if (!formGeoJson?.coordinates?.[0]) return false

    const existingCoords = feature.geometry.coordinates?.[0]
    const formCoords = formGeoJson.coordinates[0]

    if (!existingCoords || !formCoords || existingCoords.length !== formCoords.length) return false

    const [existingFirst, formFirst] = [existingCoords[0], formCoords[0]]
    return (
      Math.abs(existingFirst[0] - formFirst[0]) < 0.0001 &&
      Math.abs(existingFirst[1] - formFirst[1]) < 0.0001
    )
  } catch {
    return false
  }
}

const shouldRemovePolygon = (existingFeature, currentPolygons, existingTerraDrawIds, geometry) => {
  if (existingTerraDrawIds.has(existingFeature.id)) return false

  const matchesById = currentPolygons.some((polygon) => matchPolygonById(existingFeature, polygon))
  if (matchesById) return false

  if (
    existingFeature.properties?.referenceId &&
    currentPolygons.some(
      (polygon) => polygon.reference_id === existingFeature.properties.referenceId
    )
  )
    return false

  if (currentPolygons.some((polygon) => matchPolygonByGeometry(existingFeature, polygon, geometry)))
    return false

  return true
}

const findExistingPolygon = (features, polygonData, newFeature) => {
  const byId = features.find(
    (f) => f.geometry?.type === 'Polygon' && matchPolygonById(f, polygonData)
  )
  if (byId) return byId

  return features.find((f) => {
    if (!f.geometry || f.geometry.type !== 'Polygon') return false
    const existingCoords = f.geometry.coordinates?.[0]
    const newCoords = newFeature.geometry.coordinates?.[0]
    if (!existingCoords || !newCoords || existingCoords.length !== newCoords.length) return false
    const [existingFirst, newFirst] = [existingCoords[0], newCoords[0]]
    return (
      Math.abs(existingFirst[0] - newFirst[0]) < 0.0001 &&
      Math.abs(existingFirst[1] - newFirst[1]) < 0.0001
    )
  })
}

const hasGeometryChanged = (existingCoords, newCoords) => {
  if (!existingCoords || !newCoords || existingCoords.length !== newCoords.length) return true
  return existingCoords.some(
    (coord, i) =>
      !newCoords[i] ||
      Math.abs(coord[0] - newCoords[i][0]) > 0.0001 ||
      Math.abs(coord[1] - newCoords[i][1]) > 0.0001
  )
}

const hasPropertiesChanged = (existingProps, newProps) => {
  return (
    existingProps.databaseId !== newProps.databaseId ||
    existingProps.referenceId !== newProps.referenceId ||
    existingProps.polygonIndex !== newProps.polygonIndex ||
    existingProps.color !== newProps.color ||
    existingProps.fillColor !== newProps.fillColor ||
    existingProps.fillOpacity !== newProps.fillOpacity ||
    existingProps.outlineColor !== newProps.outlineColor ||
    existingProps.outlineWidth !== newProps.outlineWidth ||
    existingProps.zIndex !== newProps.zIndex
  )
}

const replaceFeatureAfterAdapterFlush = (terraDraw, feature) => {
  if (typeof terraDraw?.removeFeatures !== 'function') {
    return false
  }

  terraDraw.removeFeatures([feature.id])
  requestAnimationFrame(() => {
    if (!terraDraw?.hasFeature?.(feature.id)) {
      terraDraw?.addFeatures?.([feature])
    }
  })

  return true
}

const createPolygonFeature = (polygonData, index, geoJson) => {
  const color = getSemanticPolygonColor(
    polygonData.polygon_type,
    polygonData.color || DELIVERY_AREA_COLORS[0]
  )

  return {
    type: 'Feature',
    id: polygonData.reference_id,
    geometry: geoJson,
    properties: {
      mode: 'polygon',
      polygonIndex: index,
      // Custom metadata
      color,
      databaseId: polygonData.id,
      referenceId: polygonData.reference_id,
      // Terra Draw styling properties
      fillColor: color,
      fillOpacity: getSemanticPolygonFillOpacity(
        polygonData.polygon_type,
        DEFAULT_DELIVERY_AREA_FILL_OPACITY
      ),
      outlineColor: color,
      outlineWidth: 2,
      zIndex: getZIndexForPolygonType(polygonData.polygon_type),
    },
  }
}

/**
 * Hook for loading and displaying existing polygons on the map using Terra Draw
 */
export const usePolygonLoading = ({
  isLoaded,
  maps,
  geometry,
  mapInstanceRef,
  polygonsRef,
  terraDrawRef,
  initialPolygons,
  currentPolygonsValue,
  isTerraDrawReady = false,
  selectedPolygonReferenceId,
}) => {
  const form = useFormContext()
  const isSyncingRef = useRef(false)
  const lastProcessedPolygonsRef = useRef(null)

  const handleTerraDrawChange = useCallback(
    (features) => {
      if (!terraDrawRef.current || isSyncingRef.current) return

      // Skip change events when in drawing mode to prevent accidental updates to existing polygons
      const currentMode = terraDrawRef.current.getMode()
      if (currentMode === 'polygon') {
        return
      }

      const allFeatures = terraDrawRef.current.getSnapshot()

      // Terra Draw passes feature IDs as an array of strings
      // Handle different possible formats for robustness
      const featureIds = Array.isArray(features)
        ? features
        : features instanceof Map
          ? Array.from(features.keys())
          : typeof features?.[Symbol.iterator] === 'function'
            ? Array.from(features)
            : []

      const featuresArray = featureIds
        .map((id) => (typeof id === 'string' ? allFeatures.find((f) => f.id === id) : id))
        .filter((f) => f?.geometry)

      featuresArray.forEach((feature) => {
        if (feature.geometry?.type !== 'Polygon') return

        // Find the mock polygon by terraDrawId
        const mockPolygonIdx = polygonsRef.current.findIndex((p) => p.terraDrawId === feature.id)
        if (mockPolygonIdx === -1) return

        const mockPolygon = polygonsRef.current[mockPolygonIdx]
        if (!mockPolygon || !geometry || !maps) return

        const encoded = encodePolygonFromGeoJson(feature.geometry, geometry, maps)
        if (!encoded) return

        // Use reference_id to find the correct index in the FULL form array
        // This is important when the displayed polygons are filtered
        const referenceId = feature.properties?.referenceId || mockPolygon.polygonData?.reference_id
        const currentPolygons = form.getFieldValue(FIELD_NAME) || []

        // Find polygon by reference_id for accurate form array index
        const formArrayIndex = currentPolygons.findIndex((p) => p.reference_id === referenceId)
        if (formArrayIndex === -1) return

        const currentEncoded = currentPolygons[formArrayIndex].encoded_polygon
        if (encoded === currentEncoded) return

        const updatedPolygons = [...currentPolygons]
        updatedPolygons[formArrayIndex] = {
          ...updatedPolygons[formArrayIndex],
          encoded_polygon: encoded,
          reference_id: updatedPolygons[formArrayIndex].reference_id,
        }

        form.setFieldValue(FIELD_NAME, updatedPolygons, { touch: true })
        form.setFieldMeta(FIELD_NAME, (prev) => ({ ...prev, isTouched: true }))

        mockPolygon.polygonData = updatedPolygons[formArrayIndex]
      })
    },
    [form, geometry, maps, polygonsRef, terraDrawRef]
  )

  // Remove polygons that no longer exist in form data
  const removeStalePolygons = useCallback(
    (currentPolygons, existingTerraDrawIds) => {
      if (!terraDrawRef.current) {
        return
      }

      const existingFeatures = terraDrawRef.current.getSnapshot()
      const existingPolygonFeatures = existingFeatures.filter((f) => f.geometry?.type === 'Polygon')

      // If currentPolygons is empty (form reset), remove all polygons
      let polygonsToRemove
      if (currentPolygons.length === 0) {
        polygonsToRemove = existingPolygonFeatures
      } else {
        polygonsToRemove = existingPolygonFeatures.filter((feature) =>
          shouldRemovePolygon(feature, currentPolygons, existingTerraDrawIds, geometry)
        )
      }

      if (
        polygonsToRemove.length > 0 &&
        typeof terraDrawRef.current.removeFeatures === 'function'
      ) {
        try {
          terraDrawRef.current.removeFeatures(polygonsToRemove.map((f) => f.id))
        } catch (err) {
          console.error('[removeStalePolygons] Error removing features:', err)
          toast.error('Error removing outdated polygons')
        }
      }
    },
    [geometry, terraDrawRef]
  )

  // Add or update polygons in Terra Draw
  const syncPolygonsToTerraDraw = useCallback(
    (currentPolygons) => {
      if (!terraDrawRef.current || !geometry?.encoding) {
        return
      }

      // Don't clear polygonsRef here - it's managed by the caller
      // We'll rebuild it as we process polygons
      const newPolygonsRef = []

      currentPolygons.forEach((polygonData, index) => {
        try {
          if (!polygonData.reference_id) {
            console.error(`[syncPolygonsToTerraDraw] Polygon ${index + 1} missing reference_id`)
            toast.error(`Polygon ${index + 1} missing reference_id, cannot load`)
            return
          }

          const encodedPath = polygonData.encoded_polygon || polygonData
          if (!encodedPath || typeof encodedPath !== 'string') {
            console.error(`[syncPolygonsToTerraDraw] Polygon ${index + 1} missing encoded_polygon`)
            return
          }

          const geoJson = encodedPolylineToGeoJson(encodedPath, geometry)
          if (!geoJson) {
            console.error(`[syncPolygonsToTerraDraw] Polygon ${index + 1} failed to decode`)
            return
          }

          const feature = createPolygonFeature(polygonData, index, geoJson)
          const featuresBefore = terraDrawRef.current.getSnapshot()
          const existingPolygon = findExistingPolygon(featuresBefore, polygonData, feature)

          if (existingPolygon) {
            feature.id = existingPolygon.id
            // Polygon exists in Terra Draw - update it if needed
            const existingCoords = existingPolygon.geometry?.coordinates?.[0]
            const newCoords = feature.geometry.coordinates?.[0]
            const existingProps = existingPolygon.properties || {}

            // Keep the Terra Draw feature ID stable so the Google Maps adapter does not receive
            // a delete and recreate for the same feature in one render frame.
            const colorChanged = existingProps.fillColor !== feature.properties.fillColor

            const geometryChanged = hasGeometryChanged(existingCoords, newCoords)
            const { mode: _mode, ...propertiesToUpdate } = feature.properties
            const propertiesChanged = hasPropertiesChanged(existingProps, propertiesToUpdate)
            let replacedForStyleChange = false

            if (
              geometryChanged &&
              typeof terraDrawRef.current.updateFeatureGeometry === 'function'
            ) {
              terraDrawRef.current.updateFeatureGeometry(existingPolygon.id, feature.geometry)
            } else if (geometryChanged) {
              replaceFeatureAfterAdapterFlush(terraDrawRef.current, feature)
            }

            if (
              colorChanged &&
              !geometryChanged &&
              replaceFeatureAfterAdapterFlush(terraDrawRef.current, feature)
            ) {
              replacedForStyleChange = true
            }

            if (
              !replacedForStyleChange &&
              propertiesChanged &&
              typeof terraDrawRef.current.updateFeatureProperties === 'function'
            ) {
              terraDrawRef.current.updateFeatureProperties(existingPolygon.id, propertiesToUpdate)
            }
          } else {
            // Polygon doesn't exist in Terra Draw - add it
            terraDrawRef.current.addFeatures([feature])
          }

          newPolygonsRef.push({
            polygonIndex: index,
            polygonData,
            terraDrawId: feature.id,
          })
        } catch (err) {
          console.error(`[syncPolygonsToTerraDraw] Error processing polygon ${index + 1}:`, err)
          toast.error(`Error loading polygon ${index + 1}`)
        }
      })

      // Update polygonsRef with the new array
      polygonsRef.current = newPolygonsRef
    },
    [geometry, terraDrawRef, polygonsRef]
  )

  // Restore selection after reload
  const restoreSelection = useCallback(
    (previouslySelectedFeatureId) => {
      if (!previouslySelectedFeatureId || !terraDrawRef.current) return

      const matchingPolygon = polygonsRef.current.find(
        (p) => p.terraDrawId === previouslySelectedFeatureId
      )

      if (matchingPolygon && typeof terraDrawRef.current.selectFeature === 'function') {
        // Features should be available immediately after syncPolygonsToTerraDraw
        // Use requestAnimationFrame to ensure Terra Draw has rendered
        requestAnimationFrame(() => {
          if (terraDrawRef.current) {
            selectTerraDrawFeature(terraDrawRef.current, previouslySelectedFeatureId)
          }
        })
      }
    },
    [terraDrawRef, polygonsRef]
  )

  // Main effect: sync polygons when form data changes
  useEffect(() => {
    const hasTerraDraw = !!terraDrawRef.current
    const hasMapInstance = !!mapInstanceRef.current

    // Wait for all dependencies to be ready
    // Note: We check terraDrawRef.current directly, but isTerraDrawReady helps trigger re-runs
    if (!isLoaded || !maps || !geometry || !hasMapInstance || !hasTerraDraw) {
      return
    }

    // Read form value directly to get the latest value
    // Use currentPolygonsValue (subscription) as primary source since it triggers this effect
    // Fall back to form.getFieldValue if subscription is undefined
    const formPolygons = form.getFieldValue(FIELD_NAME)
    const subscriptionPolygons = currentPolygonsValue

    // Determine which value to use:
    // 1. If subscription has a value (even if empty array), use it (it's what triggers the effect)
    // 2. If form.getFieldValue has a value (even if empty array), use it
    // 3. Only fall back to initialPolygons if both are undefined/null (form not initialized yet)
    const polygonsToUse =
      subscriptionPolygons !== undefined && subscriptionPolygons !== null
        ? subscriptionPolygons
        : formPolygons !== undefined && formPolygons !== null
          ? formPolygons
          : initialPolygons || []

    const currentPolygons = polygonsToUse

    const polygonsKey = JSON.stringify(
      currentPolygons.map((p) => ({
        id: p.id,
        reference_id: p.reference_id,
        encoded_polygon: p.encoded_polygon,
        polygon_type: p.polygon_type,
        color: getSemanticPolygonColor(p.polygon_type, p.color),
      }))
    )

    // Check if Terra Draw state matches what we expect
    const terraDrawFeatures = terraDrawRef.current?.getSnapshot() || []
    const terraDrawPolygonCount = terraDrawFeatures.filter(
      (f) => f.geometry?.type === 'Polygon'
    ).length
    const expectedPolygonCount = currentPolygons.length

    // If keys match but Terra Draw state doesn't match, force a re-sync
    const shouldForceSync =
      lastProcessedPolygonsRef.current === polygonsKey &&
      terraDrawPolygonCount !== expectedPolygonCount

    if (lastProcessedPolygonsRef.current === polygonsKey && !shouldForceSync) {
      return
    }

    // Don't sync if we're already syncing (prevents infinite loops)
    // This happens when handleTerraDrawChange updates the form, which triggers this effect
    if (isSyncingRef.current) {
      return
    }

    lastProcessedPolygonsRef.current = polygonsKey
    isSyncingRef.current = true

    // Build a set of reference IDs that are still in the current polygons
    const currentPolygonIds = new Set(
      currentPolygons.map((p) => p.reference_id || p.id).filter(Boolean)
    )

    // Only include Terra Draw IDs for polygons that are STILL in currentPolygons
    // This ensures deleted polygons will be removed from Terra Draw
    const existingTerraDrawIds = new Set(
      polygonsRef.current
        .filter((p) => {
          const polygonId = p.polygonData?.reference_id || p.polygonData?.id
          return polygonId && currentPolygonIds.has(polygonId)
        })
        .map((p) => p.terraDrawId)
        .filter(Boolean)
    )

    const previouslySelectedFeature = terraDrawRef.current
      .getSnapshot()
      .find((f) => f.properties?.selected === true)
    const previouslySelectedFeatureId = previouslySelectedFeature?.id

    removeStalePolygons(currentPolygons, existingTerraDrawIds)

    // Clear polygonsRef for polygons that were removed
    if (currentPolygons.length === 0) {
      polygonsRef.current = []
    } else {
      // Remove entries from polygonsRef that no longer exist in currentPolygons
      // (reuse currentPolygonIds calculated above)
      polygonsRef.current = polygonsRef.current.filter((mockPolygon) => {
        const polygonId = mockPolygon.polygonData?.reference_id || mockPolygon.polygonData?.id
        return polygonId && currentPolygonIds.has(polygonId)
      })
    }

    if (currentPolygons.length > 0) {
      syncPolygonsToTerraDraw(currentPolygons)
    }

    if (terraDrawRef.current) {
      if (terraDrawRef.current.getMode() !== 'select') {
        terraDrawRef.current.setMode('select')
      }

      const selectedFeatureId =
        selectedPolygonReferenceId === undefined
          ? previouslySelectedFeatureId
          : selectedPolygonReferenceId
            ? polygonsRef.current.find(
                (polygon) => polygon.polygonData?.reference_id === selectedPolygonReferenceId
              )?.terraDrawId
            : null

      // Restore selection - use requestAnimationFrame to ensure DOM is updated
      if (selectedFeatureId) {
        requestAnimationFrame(() => {
          restoreSelection(selectedFeatureId)
          // Clear syncing flag after selection is restored
          isSyncingRef.current = false
        })
      } else {
        // Clear syncing flag immediately if no selection to restore
        isSyncingRef.current = false
      }
    }
  }, [
    isLoaded,
    maps,
    geometry,
    mapInstanceRef,
    terraDrawRef,
    polygonsRef,
    initialPolygons,
    currentPolygonsValue,
    removeStalePolygons,
    syncPolygonsToTerraDraw,
    restoreSelection,
    isTerraDrawReady,
    form,
    selectedPolygonReferenceId,
  ])

  // Set up Terra Draw change listener once when Terra Draw is ready
  // Keep it separate from the polygon sync effect to avoid removing/re-adding it
  // on every form update, which can cause events to be missed
  useEffect(() => {
    const terraDraw = terraDrawRef.current
    if (!terraDraw || !isTerraDrawReady) {
      return
    }

    terraDraw.on('change', handleTerraDrawChange)

    return () => {
      if (terraDraw) {
        terraDraw.off('change', handleTerraDrawChange)
      }
    }
  }, [terraDrawRef, isTerraDrawReady, handleTerraDrawChange])
}
